import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Group,
  Stack,
  Text,
  Button,
  Center,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconMessages, IconClock } from "@tabler/icons-react";
import type {
  Conversation,
  Message,
  MessageMediaRef,
} from "../../types/messaging";
import { useMessaging } from "../../context/MessagingContext";
import { useSalon } from "../../context/SalonContext";
import BrandedLoadingOverlay from "../common/BrandedLoadingOverlay";
import { ConversationAvatar } from "./ConversationAvatar";
import { MessageBubble } from "./MessageBubble";
import { ImageLightbox } from "./ImageLightbox";
import { Composer } from "./Composer";
import { ConversationHeaderActions } from "./ConversationHeaderActions";
import { ScheduledPanel } from "./ScheduledPanel";
import { useScheduledMessages } from "./useScheduledMessages";
import PageHeaderBar from "../common/PageHeaderBar/PageHeaderBar";
import { conversationDisplayName, conversationSubtitle } from "./chatHelpers";
import { groupMessagesByDay } from "../../utils/groupMessagesByDay";
import { computeMessageClusterFlags } from "../../utils/messageClustering";
import styles from "./ConversationView.module.css";

interface ConversationViewProps {
  conversation: Conversation | null;
  messages: Message[];
  /** Opening/switching to this thread — shows the branded feed overlay. */
  loading: boolean;
  /** A "load earlier" page is in flight — shows the button spinner only. */
  loadingEarlier?: boolean;
  hasMore: boolean;
  /** Business timezone — scheduling only (compose/show future sends in the
   *  salon's tz). Thread message times render in the viewer's local tz. */
  timezone: string;
  onSend: (body: string, media?: MessageMediaRef[]) => Promise<void>;
  onLoadEarlier?: () => void;
  /** Mobile drill-down: render a back bar that pops to the list. */
  showBackBar?: boolean;
  onBack?: () => void;
}

export const ConversationView: React.FC<ConversationViewProps> = ({
  conversation,
  messages,
  loading,
  loadingEarlier = false,
  hasMore,
  timezone,
  onSend,
  onLoadEarlier,
  showBackBar,
  onBack,
}) => {
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const { uploadMedia } = useMessaging();
  // The salon name(s) the backend prepends to outbound SMS as "[Name] " — passed
  // to each bubble so that tag is hidden in the thread.
  const { businessInfo } = useSalon() as {
    businessInfo: { salon_name?: string; legal_business_name?: string } | null;
  };
  const businessName = businessInfo?.salon_name ?? "";
  const legalBusinessName = businessInfo?.legal_business_name ?? "";
  const [manageOpen, manage] = useDisclosure(false);
  // A single lightbox instance for the whole thread (bubbles open it by URL).
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  // Chat scheduled sends + the client's automated appointment scheduled messages,
  // merged. Drives both the composer banner count and the drawer list (one fetch).
  const scheduled = useScheduledMessages(conversation);

  // Keep the newest message in view as the thread grows / a reply is sent.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length, conversation?.conversation_id]);

  if (!conversation) {
    return (
      <Center className={styles.empty}>
        <Stack align="center" gap="xs">
          <IconMessages size={40} stroke={1.5} opacity={0.5} />
          <Text c="dimmed">Select a conversation to start messaging.</Text>
        </Stack>
      </Center>
    );
  }

  const groups = groupMessagesByDay(messages);

  return (
    <Box className={styles.view}>
      {showBackBar && onBack && (
        <PageHeaderBar label="All chats" onBack={onBack} />
      )}

      <Group
        className={styles.header}
        gap="sm"
        wrap="nowrap"
        justify="space-between"
      >
        <Group gap="sm" wrap="nowrap" className={styles.headerIdentity}>
          <ConversationAvatar conversation={conversation} size={40} />
          <Stack gap={0} className={styles.headerText}>
            <Text className={styles.headerName} truncate>
              {conversationDisplayName(conversation)}
            </Text>
            <Text className={styles.headerSub} truncate>
              {conversationSubtitle(conversation)}
            </Text>
          </Stack>
        </Group>
        <ConversationHeaderActions
          conversation={conversation}
          onOpenScheduled={manage.open}
        />
      </Group>

      <Box className={styles.feedWrapper}>
        {/* Covers only the feed while the thread loads on open/switch, so the
            header above stays visible with whatever we already know. */}
        <BrandedLoadingOverlay
          visible={loading}
          background="var(--surface-page)"
        />
        <Box className={styles.feed}>
          <Stack gap="md" className={styles.feedInner}>
            {hasMore && (
              <Center>
                <Button
                  variant="subtle"
                  size="xs"
                  color="teal"
                  onClick={onLoadEarlier}
                  loading={loadingEarlier}
                >
                  Load earlier messages
                </Button>
              </Center>
            )}
            {groups.map((group) => {
              // Cluster back-to-back messages within each day: tight spacing
              // inside a cluster, one timestamp on the cluster's most recent
              // bubble, and extra separation where a new cluster begins.
              const flags = computeMessageClusterFlags(group.messages);
              return (
                <Stack key={group.key} gap="xs">
                  <Center>
                    <Text className={styles.dayDivider}>{group.label}</Text>
                  </Center>
                  <Stack gap={2}>
                    {group.messages.map((message, i) => (
                      <Box
                        key={message.uuid}
                        className={
                          flags[i].isClusterStart && i !== 0
                            ? styles.clusterStart
                            : undefined
                        }
                      >
                        <MessageBubble
                          message={message}
                          showMeta={flags[i].showMeta}
                          onOpenImage={setLightboxUrl}
                          businessName={businessName}
                          legalBusinessName={legalBusinessName}
                        />
                      </Box>
                    ))}
                  </Stack>
                </Stack>
              );
            })}
            <div ref={bottomRef} />
          </Stack>
        </Box>
      </Box>

      {scheduled.count > 0 && (
        <UnstyledButton
          className={styles.scheduledBanner}
          onClick={manage.open}
          aria-label={`View ${scheduled.count} scheduled ${
            scheduled.count === 1 ? "message" : "messages"
          }`}
        >
          <Group gap="xs" wrap="nowrap" justify="center">
            <IconClock size={16} />
            <Text size="sm" fw={500}>
              {scheduled.count === 1
                ? "There is 1 scheduled message. Click here to view."
                : `There are ${scheduled.count} scheduled messages. Click here to view.`}
            </Text>
          </Group>
        </UnstyledButton>
      )}

      <Composer
        onSend={onSend}
        onUploadImage={uploadMedia}
        conversationId={conversation.conversation_id}
        timezone={timezone}
      />

      <ScheduledPanel
        opened={manageOpen}
        onClose={manage.close}
        timezone={timezone}
        scheduled={scheduled}
      />

      <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
    </Box>
  );
};

export default ConversationView;
